import { useCallback, useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { useSelector, useDispatch } from 'react-redux';
import { ErrorBox, Loader } from '@/components/common';
import { workdayTasksIsLoadingSelector } from '@/redux/loading';
import classNames from 'classnames';
import { clearWorkdayTasksErrorsAction, repsWorkdayTasksSelector, updateRepsWorkdayTaskAsync, repsWorkdayTasksUpdateErrorSelector, requestRepsWorkdayTasksAsync } from '@/redux/reps';
import WorkdayTasksListItem from './WorkdayTasksListItem';
import { hrDataSelector } from '@/redux/onboarding';
import { ConfirmationModal } from '@/components/modals';

const WorkdayTasksList = ({
  isEditable,
  userId,
  recruitingSeasonId,
  onboarding,
}) => {
  const dispatch = useDispatch();

  const loading = useSelector((state) => workdayTasksIsLoadingSelector(state));
  const error = useSelector((state) => repsWorkdayTasksUpdateErrorSelector(state, userId));
  const workdayTasks = useSelector((state) => repsWorkdayTasksSelector(state, userId));

  const hrData = useSelector(hrDataSelector);
  const canCheckI9 = useMemo(
    () => !hrData?.i9EnvelopeSent, // Can't modify I9 task if sent already.
    [hrData],
  );
  const disabledTasks = useMemo(
    () => {
      const disabled = [];
      if (!canCheckI9) {
        disabled.push('i9');
      }

      return disabled;
    }
    , [canCheckI9],
  );

  useEffect(() => {
    if (userId) {
      dispatch(clearWorkdayTasksErrorsAction({ userId }));
      dispatch(requestRepsWorkdayTasksAsync.request({ userId, recruitingSeasonId, disabledTasks }));
    }
  }, [dispatch, userId, recruitingSeasonId, disabledTasks]);

  const rows = [];

  const [confirmationOpen, setConfirmationOpen] = useState(false);
  const [confirmationTask, setConfirmationTask] = useState(null);

  const updateWorkdayTask = useCallback((taskName, taskCompleted) => {
    const payload = {
      tasks: [{ taskCompleted, taskName }],
      disabledTasks,
    };
    if (userId) {
      payload.userId = userId;
      payload.recruitingSeasonId = recruitingSeasonId;
    }
    dispatch(updateRepsWorkdayTaskAsync.request(payload));
  }, [disabledTasks, userId, dispatch, recruitingSeasonId]);

  const handleChangeTask = useCallback((event, confirmationText) => {
    if (confirmationText && event.target.checked) {
      setConfirmationTask({
        taskName: event.target.name,
        taskCompleted: event.target.checked,
        confirmationText,
      });
      setConfirmationOpen(true);

      return;
    }

    updateWorkdayTask(event.target.name, event.target.checked);
  }, [updateWorkdayTask]);

  const onConfirmationOk = useCallback(() => {
    if (confirmationTask) {
      updateWorkdayTask(confirmationTask.taskName, confirmationTask.taskCompleted);
    }
    setConfirmationOpen(false);
    setConfirmationTask(null);
  }, [confirmationTask, updateWorkdayTask]);

  const onConfirmationCancel = useCallback(() => {
    setConfirmationOpen(false);
    setConfirmationTask(null);
  }, []);

  if (workdayTasks) {
    workdayTasks.forEach((workdayTask, key) => {
      rows.push(
        <WorkdayTasksListItem
          taskName={workdayTask.taskName}
          taskCompleted={workdayTask.taskCompleted}
          taskLabel={workdayTask.taskLabel}
          key={`workdayTask_${key}`}
          handleChangeTask={(event) => handleChangeTask(event, workdayTask.confirmationText || '')}
          disabled={!isEditable || workdayTask.disabled}
        />,
      );
    });
  }

  return (
    <div className={classNames(
      onboarding ? 'px-6 py-5 border-gray-200 sm:px-6' : '',
    )}
    >
      {loading
        ? <Loader />
        : (
          <>
            {error && (
              <div className="mb-2">
                <ErrorBox message={error} />
              </div>
            )}
            {rows}
          </>
        )}
      <ConfirmationModal
        isOpened={confirmationOpen}
        modalWidth="max-w-[592px] w-full"
        onCancel={onConfirmationCancel}
        onAction={onConfirmationOk}
        title="Are you sure?"
        message={confirmationTask?.confirmationText || ''}
        cancelLabel="Cancel"
        confirmLabel="Confirm"
        confirmButtonColor="aptiveblue"
      />
    </div>
  );
};

WorkdayTasksList.propTypes = {
  isEditable: PropTypes.bool,
  userId: PropTypes.any,
  recruitingSeasonId: PropTypes.any,
  onboarding: PropTypes.bool,
};

export default WorkdayTasksList;
